// ===================== Chunk 1: Utilities, Focus, Polyfills, Shopify Helpers =====================

// Returns all focusable elements within a container
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// Section ID helper
class SectionId {
  static #separator = '__';

  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

// HTML update helper with double buffer
class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // Deduplicate IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));
    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// ===================== Focus Trap =====================
const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

// ===================== Focus-visible Polyfill =====================
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB',
    'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN'
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) mouseClick = false;
  });
  window.addEventListener('mousedown', () => (mouseClick = true));
  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
    if (mouseClick) return;
    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');
  }, true);
}

// ===================== Pause All Media =====================
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach(video => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach(video => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach(video => video.pause());
  document.querySelectorAll('product-model').forEach(model => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

// ===================== Shopify Helpers =====================
if (typeof window.Shopify === 'undefined') window.Shopify = {};

Shopify.bind = function (fn, scope) {
  return function () { return fn.apply(scope, arguments); };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0; i < selector.options.length; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options = {}) {
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};
  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);
  for (let key in params) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    const value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },
  initProvince: function () {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) Shopify.setSelectorByValue(this.provinceEl, value);
  },
  countryHandler: function () {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    const raw = opt.getAttribute('data-provinces');
    const provinces = JSON.parse(raw);
    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length === 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      provinces.forEach(([val, label]) => {
        const optEl = document.createElement('option');
        optEl.value = val;
        optEl.innerHTML = label;
        this.provinceEl.appendChild(optEl);
      });
      this.provinceContainer.style.display = '';
    }
  },
  clearOptions: function (selector) { while (selector.firstChild) selector.removeChild(selector.firstChild); },
  setOptions: function (selector, values) {
    values.forEach((val) => {
      const optEl = document.createElement('option');
      optEl.value = val;
      optEl.innerHTML = val;
      selector.appendChild(optEl);
    });
  },
};
// ===================== Chunk 2: QuantityInput, Debounce/Throttle, Fetch Helpers =====================

// ===================== QuantityInput =====================
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="number"]');
    this.buttonMinus = this.querySelector('[data-action="decrease"]');
    this.buttonPlus = this.querySelector('[data-action="increase"]');

    this.buttonMinus?.addEventListener('click', () => this.updateQuantity(-1));
    this.buttonPlus?.addEventListener('click', () => this.updateQuantity(1));
  }

  updateQuantity(change) {
    let value = parseInt(this.input.value, 10);
    const min = parseInt(this.input.min, 10) || 0;
    const max = parseInt(this.input.max, 10) || Infinity;

    value += change;
    if (value < min) value = min;
    if (value > max) value = max;
    this.input.value = value;

    // Trigger input event for Shopify listeners
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('quantity-input', QuantityInput);

// ===================== Debounce & Throttle =====================
function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, wait) {
  let lastTime = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastTime >= wait) {
      lastTime = now;
      fn.apply(this, args);
    }
  };
}

// ===================== Fetch Helpers =====================
class FetchHelper {
  static async getJSON(url, options = {}) {
    try {
      const res = await fetch(url, { ...options, headers: { 'Accept': 'application/json', ...options.headers } });
      return await res.json();
    } catch (err) {
      console.error('Fetch JSON error:', err);
      return null;
    }
  }

  static async postJSON(url, data = {}, options = {}) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...options.headers },
        ...options,
      });
      return await res.json();
    } catch (err) {
      console.error('Post JSON error:', err);
      return null;
    }
  }

  static async fetchHTML(url, options = {}) {
    try {
      const res = await fetch(url, { ...options });
      return await res.text();
    } catch (err) {
      console.error('Fetch HTML error:', err);
      return '';
    }
  }
}

// ===================== Shopify Form Helpers =====================
Shopify.updateCartNote = async function (note) {
  return await FetchHelper.postJSON('/cart/update.js', { note });
};

Shopify.addItemToCart = async function (variantId, quantity = 1) {
  return await FetchHelper.postJSON('/cart/add.js', { id: variantId, quantity });
};

Shopify.changeItemQuantity = async function (line, quantity) {
  return await FetchHelper.postJSON('/cart/change.js', { line, quantity });
};

Shopify.getCart = async function () {
  return await FetchHelper.getJSON('/cart.js');
};

Shopify.clearCart = async function () {
  return await FetchHelper.postJSON('/cart/clear.js');
};
// ===================== Chunk 3: Drawer & Modal Components =====================

// ===================== MenuDrawer =====================
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', (e) => this.onSummaryClick(e))
    );

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (e) => this.closeSubmenu(button.closest('details')))
    );

    this.addEventListener('keyup', (e) => this.onKeyUp(e));
    this.addEventListener('focusout', () => this.onFocusOut());
  }

  onSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    const isOpen = details.hasAttribute('open');

    if (details === this.mainDetailsToggle) {
      isOpen ? this.closeMenuDrawer(null, summary) : this.openMenuDrawer(summary);
    } else {
      setTimeout(() => {
        details.classList.add('menu-opening');
        summary.setAttribute('aria-expanded', true);
      }, 100);
    }
  }

  openMenuDrawer(summary) {
    this.mainDetailsToggle.classList.add('menu-opening');
    summary.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summary);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => details.removeAttribute('open'));
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
  }

  closeSubmenu(details) {
    if (!details) return;
    details.classList.remove('menu-opening');
    details.querySelector('summary')?.setAttribute('aria-expanded', false);
    removeTrapFocus(details.querySelector('summary'));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.closeMenuDrawer();
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }
}

customElements.define('menu-drawer', MenuDrawer);

// ===================== HeaderDrawer =====================
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summary) {
    this.header = this.header || document.querySelector('.section-header');
    this.header.classList.add('menu-open');
    super.openMenuDrawer(summary);
    window.addEventListener('resize', this.onResize);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

// ===================== ModalDialog =====================
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    closeButton?.addEventListener('click', () => this.hide(false));

    this.addEventListener('keyup', (e) => e.code.toUpperCase() === 'ESCAPE' && this.hide());

    this.addEventListener('click', (e) => {
      if (e.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (!this.moved) {
      this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
      document.body.appendChild(this);
      this.moved = true;
    }
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

customElements.define('modal-dialog', ModalDialog);

// ===================== BulkModal =====================
class BulkModal extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver((entries, obs) => {
      if (!entries[0].isIntersecting) return;
      obs.unobserve(this);
      if (!this.innerHTML.trim()) {
        const url = this.dataset.url.split('?')[0];
        fetch(`${url}?section_id=bulk-quick-order-list`)
          .then((res) => res.text())
          .then((htmlString) => {
            const html = new DOMParser().parseFromString(htmlString, 'text/html');
            const source = html.querySelector('.quick-order-list-container')?.parentNode;
            if (source) this.innerHTML = source.innerHTML;
          });
      }
    });
    observer.observe(this);
  }
}

customElements.define('bulk-modal', BulkModal);

// ===================== ModalOpener =====================
class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    button?.addEventListener('click', () => {
      const modal = document.querySelector(this.dataset.modal);
      modal?.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

// ===================== DeferredMedia =====================
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    poster?.addEventListener('click', () => this.loadContent(true));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.hasAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      const media = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) media.focus();
      if (media?.nodeName === 'VIDEO' && media.getAttribute('autoplay')) media.play();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);
// ===================== Chunk 4: Slider & Product Components =====================

// ===================== SliderComponent =====================
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[data-slider]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.currentIndex = 0;

    this.prevButton?.addEventListener('click', () => this.prev());
    this.nextButton?.addEventListener('click', () => this.next());
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => slide.classList.toggle('active', i === index));
    this.currentIndex = index;
  }

  prev() {
    const newIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.showSlide(newIndex);
  }

  next() {
    const newIndex = (this.currentIndex + 1) % this.slides.length;
    this.showSlide(newIndex);
  }
}

customElements.define('slider-component', SliderComponent);

// ===================== SlideshowComponent =====================
class SlideshowComponent extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('[data-slideshow-slide]'));
    this.prevButton = this.querySelector('[data-slideshow-prev]');
    this.nextButton = this.querySelector('[data-slideshow-next]');
    this.currentIndex = 0;

    this.prevButton?.addEventListener('click', () => this.prev());
    this.nextButton?.addEventListener('click', () => this.next());
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => slide.classList.toggle('visible', i === index));
    this.currentIndex = index;
  }

  prev() {
    const newIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.showSlide(newIndex);
  }

  next() {
    const newIndex = (this.currentIndex + 1) % this.slides.length;
    this.showSlide(newIndex);
  }
}

customElements.define('slideshow-component', SlideshowComponent);

// ===================== VariantSelects =====================
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.selects = Array.from(this.querySelectorAll('select'));
    this.productForm = this.querySelector('form');
    this.selects.forEach((select) => select.addEventListener('change', () => this.onChange()));
  }

  onChange() {
    const selectedOptions = this.selects.map((select) => select.value);
    this.updateVariant(selectedOptions);
  }

  updateVariant(selectedOptions) {
    const variants = JSON.parse(this.dataset.variants || '[]');
    const matchingVariant = variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (matchingVariant) {
      this.productForm.querySelector('[name="id"]').value = matchingVariant.id;
      this.dispatchEvent(new CustomEvent('variantChange', { detail: matchingVariant }));
    }
  }
}

customElements.define('variant-selects', VariantSelects);

// ===================== ProductRecommendations =====================
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;

    fetch(`${this.dataset.url}?section_id=product-recommendations`)
      .then((res) => res.text())
      .then((htmlString) => {
        const html = new DOMParser().parseFromString(htmlString, 'text/html');
        const section = html.querySelector('.product-recommendations');
        if (section) this.innerHTML = section.innerHTML;
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);
// ===================== Chunk 5: Cart & Performance Helpers =====================

// ===================== BulkAdd =====================
class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    if (!this.requestStarted) this.sendQueue();
  }

  sendQueue() {
    if (!this.queue.length) return;
    this.requestStarted = true;

    const items = {};
    this.queue.forEach((item) => (items[item.id] = item.quantity));
    this.queue = [];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: Object.entries(items).map(([id, qty]) => ({ id: parseInt(id), quantity: qty })) }),
    })
      .then(() => {
        this.requestStarted = false;
        if (this.queue.length) this.sendQueue();
      })
      .catch(() => (this.requestStarted = false));
  }
}

customElements.define('bulk-add', BulkAdd);

// ===================== CartPerformance =====================
class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    performance.mark(`${metricName}:start`, { startTime: event.timeStamp });
    performance.mark(`${metricName}:end`);
    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    performance.mark(`${metricName}:end`);
    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }
}

// ===================== CartDrawer =====================
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector('[data-cart-close]');
    this.drawer = this.querySelector('[data-cart-drawer]');
    this.closeButton?.addEventListener('click', () => this.close());
  }

  open() {
    this.drawer?.classList.add('open');
    document.body.classList.add('overflow-hidden-cart');
  }

  close() {
    this.drawer?.classList.remove('open');
    document.body.classList.remove('overflow-hidden-cart');
  }
}

customElements.define('cart-drawer', CartDrawer);

// ===================== CartNotification =====================
class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector('[data-notification-close]');
    this.closeButton?.addEventListener('click', () => this.hide());
  }

  show(message) {
    this.querySelector('.notification-message').textContent = message;
    this.setAttribute('open', '');
  }

  hide() {
    this.removeAttribute('open');
  }
}



// ===================== Global Helpers =====================

// Pause all media helper
function pauseAllMedia() {
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('.js-youtube, .js-vimeo').forEach((iframe) =>
    iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
  );
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

// Debounce and throttle
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
}

// Fetch config
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}
